import { useFieldActions, useForm } from 'form-atoms'
import { useSetAtom } from 'jotai'
import { usePathname, useRouter } from 'next/navigation'
import { useQueryStates } from 'nuqs'
import { startTransition, useCallback, useMemo } from 'react'
import {
  fromSearchParamsParsers,
  toSearchParamsParsers,
} from '@/src/stores/params'
import type { UseTokenFieldActionsProps } from '@/src/stores/swap/actions'
import { useTokenFieldActions } from '@/src/stores/swap/actions'
import { maxBalanceStateAtom, swapFormAtom } from '@/src/stores/swap/atoms'

interface Token {
  chain_id?: string | null | undefined
  denom?: string | null | undefined
}
type RouteProps = Token & {
  ticker?: string | null
  direction?: UseTokenFieldActionsProps['direction'] | 'context-switch'
}

function tokenInfoPageUrl({
  newRoute,
  currentFrom,
  currentTo,
}: {
  newRoute: RouteProps
  currentFrom?: Token
  currentTo?: Token
}) {
  const params = new URLSearchParams()

  // Always add ticker to query params
  if (newRoute.ticker) {
    params.set('ticker', newRoute.ticker)
  }

  const fromPart = currentFrom
    ? `fromToken=${currentFrom.denom}&fromChain=${currentFrom.chain_id}`
    : ''
  const toPart = currentTo
    ? `toToken=${currentTo.denom}&toChain=${currentTo.chain_id}`
    : ''

  if (newRoute.direction === 'context-switch') {
    // Context switch: Only change ticker in URL, keep other query params unchanged
    // This swaps foreground/background display without affecting trade module
    if (toPart) params.set('toToken', currentTo!.denom!)
    if (toPart) params.set('toChain', currentTo!.chain_id!)
    if (fromPart) params.set('fromToken', currentFrom!.denom!)
    if (fromPart) params.set('fromChain', currentFrom!.chain_id!)
    return `/tokens?${params.toString()}`
  }
  if (newRoute.direction === 'from') {
    if (toPart) params.set('toToken', currentTo!.denom!)
    if (toPart) params.set('toChain', currentTo!.chain_id!)
    if (newRoute.denom) params.set('fromToken', newRoute.denom)
    if (newRoute.chain_id) params.set('fromChain', newRoute.chain_id)
    return `/tokens?${params.toString()}`
  }
  if (newRoute.direction === 'to') {
    if (newRoute.denom) params.set('toToken', newRoute.denom)
    if (newRoute.chain_id) params.set('toChain', newRoute.chain_id)
    if (fromPart) params.set('fromToken', currentFrom!.denom!)
    if (fromPart) params.set('fromChain', currentFrom!.chain_id!)
    return `/tokens?${params.toString()}`
  }
  return `/tokens?${params.toString()}`
}

export function useTokenActions({
  direction,
}: {
  direction?: UseTokenFieldActionsProps['direction']
} = {}) {
  const router = useRouter()
  const pathname = usePathname()
  const isTokenPage = pathname.startsWith('/tokens')
  const isHomepage = pathname === '/'
  const isInsightsPage = pathname.startsWith('/insights')
  const [{ toToken: toDenom, toChain, fromToken: fromDenom, fromChain }] =
    useQueryStates({
      ...fromSearchParamsParsers,
      ...toSearchParamsParsers,
    })

  const setMaxBalanceState = useSetAtom(maxBalanceStateAtom)

  // Get direct access to form field setters for optimistic updates
  const { fieldAtoms } = useForm(swapFormAtom)
  const { setValue: setFromValueDirect } = useFieldActions(fieldAtoms.from)
  const { setValue: setToValueDirect } = useFieldActions(fieldAtoms.to)

  // Always call useTokenFieldActions to comply with React hooks rules
  // Pass a default 'from' direction if none provided
  const { setValue } = useTokenFieldActions({ direction: direction ?? 'from' })

  const fromToken = useMemo(
    (): Token | undefined =>
      fromDenom || fromChain
        ? {
            denom: fromDenom,
            chain_id: fromChain,
          }
        : undefined,
    [fromDenom, fromChain],
  )
  const toToken = useMemo(
    (): Token | undefined =>
      toDenom || toChain
        ? {
            denom: toDenom,
            chain_id: toChain,
          }
        : undefined,
    [toDenom, toChain],
  )

  const pushRoute = useCallback(
    ({ chain_id, denom, ticker, direction = 'from' }: RouteProps) => {
      if (!ticker) return

      const url = tokenInfoPageUrl({
        newRoute: { chain_id, denom, ticker, direction },
        currentFrom: fromToken,
        currentTo: toToken,
      })

      // Use startTransition to mark navigation as non-urgent, keeping UI responsive
      startTransition(() => {
        router.push(url, { scroll: false })
      })

      if (direction === 'from') {
        setMaxBalanceState('default')
      }
    },
    [router, fromToken, toToken, setMaxBalanceState],
  )

  const prefetchRoute = useCallback(
    ({ chain_id, denom, ticker, direction = 'from' }: RouteProps) => {
      if (!ticker) return

      router.prefetch(
        tokenInfoPageUrl({
          newRoute: { chain_id, denom, ticker, direction },
          currentFrom: fromToken,
          currentTo: toToken,
        }),
      )
    },
    [router, fromToken, toToken],
  )

  const getTokenInfoUrl = useCallback(
    ({ chain_id, denom, ticker, direction = 'from' }: RouteProps) => {
      if (!ticker) return
      return tokenInfoPageUrl({
        newRoute: { chain_id, denom, ticker, direction },
        currentFrom: fromToken,
        currentTo: toToken,
      })
    },
    [fromToken, toToken],
  )

  const swapTokensRoute = useCallback(
    ({
      chain_id,
      denom,
      ticker,
    }: {
      chain_id: string
      denom: string
      ticker: string
    }) => {
      if (!ticker) return

      // When swapping: current FROM becomes new TO, current TO becomes new FROM
      const newToToken = { denom, chain_id }
      const newFromToken = toToken

      router.push(
        tokenInfoPageUrl({
          newRoute: { chain_id, denom, ticker, direction: 'to' },
          currentFrom: newFromToken,
          currentTo: newToToken,
        }),
      )
    },
    [router, toToken],
  )

  /**
   * Unified token selection action.
   * Automatically decides whether to update URL (navigate) or just update form state.
   *
   * Use this for:
   * - Token selection in swap form
   * - Selecting from search results
   * - Selecting from portfolio balances
   *
   * Logic:
   * - On token pages + ticker available → Navigate (clicked token becomes the foreground/ticker)
   * - On homepage/insights → Navigate (clicked token becomes destination)
   * - Otherwise → Update form only
   */
  const selectToken = useCallback(
    ({
      chain_id,
      denom,
      ticker,
    }: {
      chain_id?: string
      denom?: string
      ticker?: string
    }) => {
      // OPTIMISTIC UPDATE: Update form state IMMEDIATELY for instant visual feedback
      // This makes the swap form feel instant while URL/queries update in background
      const effectiveDirection = isTokenPage ? (direction ?? 'to') : 'to'

      if (chain_id && denom) {
        if (effectiveDirection === 'from') {
          setFromValueDirect({ chain: chain_id, token: denom })
        } else {
          setToValueDirect({ chain: chain_id, token: denom })
        }
      }

      // Then handle navigation (URL updates happen in background via startTransition)
      // On token pages, selecting any token should update the URL (navigate to that token's page)
      // The clicked token becomes the ticker (foreground)
      if (isTokenPage && ticker) {
        pushRoute({ chain_id, denom, ticker, direction: effectiveDirection })
      } else if (isHomepage || isInsightsPage) {
        // On homepage or insights, selecting a token should update the URL (navigate to that token's page)
        pushRoute({ chain_id, denom, ticker, direction: 'to' })
      } else {
        // Otherwise, just update the form field without navigation
        setValue({ chain_id, denom })
      }
    },
    [
      isTokenPage,
      isHomepage,
      isInsightsPage,
      direction,
      pushRoute,
      setValue,
      setFromValueDirect,
      setToValueDirect,
    ],
  )

  // Check if switch button should be disabled
  // Only disable if query params are missing (not based on loading state)
  const isSwitchDisabled = useMemo(() => {
    return !fromChain || !toChain
  }, [fromChain, toChain])

  return {
    selectToken, // NEW: Unified action for token selection
    getTokenInfoUrl, // For building URLs
    pushRoute, // For direct navigation (e.g., clicking token cards)
    prefetchRoute, // For prefetching
    swapTokensRoute, // For swap button
    isSwitchDisabled, // For disabling switch button when data is not loaded
  }
}
